/**
 * Confirm dialog for license actions.
 *
 * Extracted from LicenseManagementPage.tsx (Sub-PR 6.3).
 */

import { useState } from 'react'

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  confirmStyle,
  onClose,
  onConfirm,
}: {
  title: string
  message: string
  confirmLabel: string
  confirmStyle: 'danger' | 'warning' | 'success'
  onClose: () => void
  onConfirm: () => Promise<void>
}): React.ReactElement {
  const [confirming, setConfirming] = useState(false)

  const handleConfirm = async (): Promise<void> => {
    setConfirming(true)
    try {
      await onConfirm()
      onClose()
    } catch {
      // Error handled by parent
    } finally {
      setConfirming(false)
    }
  }

  const buttonColors = {
    danger: 'bg-red-600 hover:bg-red-700',
    warning: 'bg-amber-600 hover:bg-amber-700',
    success: 'bg-green-600 hover:bg-green-700',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-stone-800">
        <div className="p-6">
          <h2 className="mb-2 text-lg font-semibold text-stone-900 dark:text-stone-100">{title}</h2>
          <p className="text-stone-600 dark:text-stone-400">{message}</p>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-700"
            >
              Cancel
            </button>
            <button
              onClick={(...args: Parameters<typeof handleConfirm>) => {
                void handleConfirm(...args)
              }}
              disabled={confirming}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50 ${buttonColors[confirmStyle]}`}
            >
              {confirming ? 'Processing...' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
